import React, { useState } from 'react';
import { STRRoom } from './types';
import { formatNumber, calcMonthSettlement, calcSubleaseRent } from './utils';
import { API_BASE_URL } from '../config';
import { useResponsive } from '../hooks/useResponsive';

interface Props {
  room: STRRoom;
  token: string;
  onRoomUpdated: (room: STRRoom) => void;
}

export default function SettlementDashboard({ room, token, onRoomUpdated }: Props) {
  const { isMobile } = useResponsive();
  const now = new Date();
  const [settleYear, setSettleYear] = useState(now.getFullYear());
  const [settleMonth, setSettleMonth] = useState(now.getMonth());
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});
  const [expandedExpenseYears, setExpandedExpenseYears] = useState<Set<number>>(new Set());
  const [popupData, setPopupData] = useState<any>(null);

  const bookings = (room.bookings || []).filter((b) => b.status !== 'cancelled');
  const opStart = room.operation_start || null;
  const isSublease = room.ownership_type === '전대 동의';
  const subleaseRentFull = isSublease ? (room.sublease_rent || 0) : 0;
  const monthlyExpenses = room.monthly_expenses || {};

  // 마지막 퇴실일 기준으로 마지막 달 월세 일할 계산
  let lastBookingCheckout = '';
  bookings.forEach((b) => { if (b.check_out && b.check_out > lastBookingCheckout) lastBookingCheckout = b.check_out; });

  const toggleCard = (key: string) => setExpandedCards((p) => ({ ...p, [key]: !p[key] }));

  // Monthly settlement
  const ms = calcMonthSettlement(bookings, settleYear, settleMonth, opStart);
  const msKey = `${settleYear}-${String(settleMonth + 1).padStart(2, '0')}`;
  const msMaintFee = monthlyExpenses[msKey]?.maintenance_fee || 0;
  const msSubleaseRent = calcSubleaseRent(subleaseRentFull, settleYear, settleMonth, opStart);
  const msIncome = ms.gross + ms.negAdj;
  const msExpense = ms.platformFee;
  const msNet = msIncome - msExpense;
  const msRentExpense = msSubleaseRent + msMaintFee;
  const msProfit = msNet - msRentExpense - ms.cleaningExpense;

  // Cumulative — 가장 이른 예약 체크인 or operation_start, 폴백 2021-01-01
  const earliestBookingDate = bookings.length > 0
    ? bookings.filter(b => b.check_in).map(b => b.check_in).sort()[0]
    : null;
  const candidates = [opStart, earliestBookingDate, '2021-01-01'].filter(Boolean) as string[];
  const cumOpStart = candidates.reduce((min, d) => d < min ? d : min);
  const opD = new Date(cumOpStart + 'T00:00:00');
  let startY = opD.getFullYear(), startM = opD.getMonth();
  let endY = now.getFullYear(), endM = now.getMonth();
  let lastCheckoutStr = cumOpStart;
  bookings.forEach((b) => {
    if (b.check_out) {
      if (b.check_out > lastCheckoutStr) lastCheckoutStr = b.check_out;
      const d = new Date(b.check_out + 'T00:00:00');
      if (d.getFullYear() > endY || (d.getFullYear() === endY && d.getMonth() > endM)) {
        endY = d.getFullYear();
        endM = d.getMonth();
      }
    }
  });

  let cumGross = 0, cumNegAdj = 0, cumCount = 0;
  let cumRent = 0, cumMaint = 0, cumClean = 0;
  let cumPlatformFee = 0, cumCleaningExpense = 0, cumOccupiedDays = 0, cumTotalDays = 0;
  let cumSubleaseRent = 0, cumMaintFee = 0;
  const monthlyBreakdown: { year: number; month: number; data: ReturnType<typeof calcMonthSettlement> }[] = [];

  for (let y = startY, m = startM; y < endY || (y === endY && m <= endM); m++) {
    if (m > 11) { m = 0; y++; }
    const mRes = calcMonthSettlement(bookings, y, m, opStart);
    cumTotalDays += mRes.availDays;
    cumOccupiedDays += mRes.occupiedDays;
    cumGross += mRes.gross; cumNegAdj += mRes.negAdj; cumCount += mRes.count;
    cumRent += mRes.rent; cumMaint += mRes.maint; cumClean += mRes.clean;
    cumPlatformFee += mRes.platformFee; cumCleaningExpense += mRes.cleaningExpense;
    const cKey = `${y}-${String(m + 1).padStart(2, '0')}`;
    if (isSublease) {
      cumSubleaseRent += calcSubleaseRent(subleaseRentFull, y, m, opStart, lastBookingCheckout || undefined);
    }
    cumMaintFee += (monthlyExpenses[cKey]?.maintenance_fee || 0);
    if (mRes.gross > 0 || mRes.count > 0) {
      monthlyBreakdown.push({ year: y, month: m, data: mRes });
    }
  }

  // 누적 가동률: 운행일 ~ 마지막 퇴거일 기준
  const lastCheckoutDate = new Date(lastCheckoutStr + 'T00:00:00');
  cumTotalDays = bookings.length > 0
    ? Math.max(0, Math.round((lastCheckoutDate.getTime() - opD.getTime()) / 86400000) + 1)
    : 0;

  const cumIncome = cumGross + cumNegAdj;
  const cumNet = cumIncome - cumPlatformFee;
  const cumRentExpense = cumSubleaseRent + cumMaintFee;
  const cumProfit = cumNet - cumRentExpense - cumCleaningExpense;
  const cumOccupancy = cumTotalDays > 0 ? Math.round(cumOccupiedDays / cumTotalDays * 100) : 0;

  // Period label (c1: opLabel + endLabel)
  const cumOpLabel = cumOpStart.substring(2, 7).replace('-', '.') + '~';
  const cumEndLabel = String(endY).substring(2) + '.' + String(endM + 1).padStart(2, '0');

  // Paid / Unpaid
  let paidTotal = 0, unpaidTotal = 0;
  bookings.forEach((b) => {
    const gross = (b.total_rent || 0) + (b.total_maintenance || 0) + (b.total_cleaning || 0);
    const amount = b.negotiated_price ? b.negotiated_price : gross;
    const net = amount - (b.platform_fee || 0);
    if (b.payment_status === 'completed') paidTotal += net;
    else unpaidTotal += net;
  });

  const cumTotalExpense = cumPlatformFee + cumCleaningExpense + cumSubleaseRent + cumMaintFee;
  const cumNetProfit = paidTotal + unpaidTotal - cumTotalExpense;
  const setupTotal = (room.setup_brokerage || 0) + (room.setup_interior || 0) + (room.setup_cleaning || 0);
  const netWithSetup = cumNetProfit - setupTotal;

  const navMonth = (delta: number) => {
    let nm = settleMonth + delta;
    let ny = settleYear;
    if (nm < 0) { nm = 11; ny--; }
    if (nm > 11) { nm = 0; ny++; }
    setSettleYear(ny);
    setSettleMonth(nm);
  };

  const monthLabel = `${settleMonth + 1}월 정산`;

  const saveMonthlyExpense = async (ym: string, value: string) => {
    const fee = parseInt(value.replace(/,/g, ''), 10) || 0;
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${room.id}/monthly-expenses/${ym}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ maintenance_fee: fee }),
      });
      if (res.ok) { const data = await res.json(); onRoomUpdated(data); }
    } catch (e) { console.error('Failed to save monthly expense:', e); }
  };

  const saveMonthlyRent = async (ym: string, value: string) => {
    const fee = parseInt(value.replace(/,/g, ''), 10) || 0;
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${room.id}/monthly-expenses/${ym}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sublease_rent: fee }),
      });
      if (res.ok) { const data = await res.json(); onRoomUpdated(data); }
    } catch (e) { console.error('Failed to save monthly rent:', e); }
  };

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e2e5e9',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    cursor: 'pointer',
  };

  return (
    <div style={{ fontSize: 12 }}>
      {/* Monthly settlement */}
      <div style={cardStyle} onClick={() => toggleCard('monthly')}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <button onClick={(e) => { e.stopPropagation(); navMonth(-1); }} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, padding: '0 4px', color: '#666' }}>◀</button>
            <span style={{ fontWeight: 500 }}>{monthLabel}</span>
            <button onClick={(e) => { e.stopPropagation(); navMonth(1); }} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, padding: '0 4px', color: '#666' }}>▶</button>
          </span>
          <span style={{ fontSize: 10, color: '#aaa' }}>{ms.occupiedDays}/{ms.availDays}일 ▼</span>
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, margin: '4px 0' }}>{formatNumber(msNet)}원</div>
        <div style={{ color: '#888', fontSize: 11 }}>{ms.count}건 · 가동률 {ms.occupancy}%</div>
        {expandedCards['monthly'] && (
          <div style={{ marginTop: 8, borderTop: '1px solid #eee', paddingTop: 8 }}>
            <Row label="수입" value={`${formatNumber(msIncome)}원`} bold />
            <Row label="임대료" value={`${formatNumber(ms.rent)}원`} />
            <Row label="관리비" value={`${formatNumber(ms.maint)}원`} />
            <Row label="청소비" value={`${formatNumber(ms.clean)}원`} />
            {ms.negAdj !== 0 && <Row label="협의 조정" value={`${formatNumber(ms.negAdj)}원`} />}
            <div style={{ borderTop: '1px solid #ddd', margin: '6px 0' }} />
            <Row label="플랫폼 수수료" value={`-${formatNumber(msExpense)}원`} />
            <div style={{ borderTop: '1px solid #ddd', margin: '6px 0' }} />
            <Row label="정산금" value={`${formatNumber(msNet)}원`} bold />
            {msSubleaseRent > 0 && <Row label="전대 월세" value={`-${formatNumber(msSubleaseRent)}원`} />}
            {msMaintFee > 0 && <Row label="월 관리비" value={`-${formatNumber(msMaintFee)}원`} />}
            {ms.cleaningExpense > 0 && <Row label="청소비 실지출" value={`-${formatNumber(ms.cleaningExpense)}원`} />}
            <div style={{ borderTop: '1px solid #ddd', margin: '6px 0' }} />
            <Row label="순수익" value={`${formatNumber(msProfit)}원`} bold />
          </div>
        )}
      </div>

      {/* Cumulative */}
      <div style={cardStyle} onClick={() => {
        const rows: any[] = [
          { label: cumOpLabel + cumEndLabel, value: '', header: true },
          { label: '수입', value: `${formatNumber(cumIncome)}원`, bold: true },
          { label: '  임대료', value: `${formatNumber(cumRent)}원` },
          { label: '  관리비', value: `${formatNumber(cumMaint)}원` },
          { label: '  청소비', value: `${formatNumber(cumClean)}원` },
        ];
        if (cumNegAdj !== 0) rows.push({ label: '  협의 조정', value: `${formatNumber(cumNegAdj)}원` });
        rows.push({ divider: true });
        rows.push({ label: '플랫폼 수수료', value: `-${formatNumber(cumPlatformFee)}원` });
        rows.push({ divider: true });
        rows.push({ label: '정산금', value: `${formatNumber(cumNet)}원`, bold: true });
        if (cumSubleaseRent > 0) rows.push({ label: '전대 월세', value: `-${formatNumber(cumSubleaseRent)}원` });
        if (cumMaintFee > 0) rows.push({ label: '월 관리비', value: `-${formatNumber(cumMaintFee)}원` });
        if (cumCleaningExpense > 0) rows.push({ label: '청소비 실지출', value: `-${formatNumber(cumCleaningExpense)}원` });
        rows.push({ divider: true });
        rows.push({ label: '순수익', value: `${formatNumber(cumProfit)}원`, bold: true });
        rows.push({ divider: true });
        monthlyBreakdown.slice().reverse().forEach((mb) => {
          const mbKey = `${mb.year}-${String(mb.month + 1).padStart(2, '0')}`;
          const mbMF = monthlyExpenses[mbKey]?.maintenance_fee || 0;
          const mbSR = calcSubleaseRent(subleaseRentFull, mb.year, mb.month, opStart, lastBookingCheckout || undefined);
          const mbIncome = mb.data.gross + mb.data.negAdj;
          const mbSettle = mbIncome - mb.data.platformFee;
          const mbProfit = mbSettle - mbSR - mbMF - mb.data.cleaningExpense;
          rows.push({ label: `${mb.year}.${String(mb.month + 1).padStart(2, '0')} (${mb.data.occupiedDays}일)`, value: `${formatNumber(mbProfit)}원` });
        });
        setPopupData({ title: '누적 정산', rows });
      }}>
        <div style={{ fontWeight: 500 }}>누적 정산</div>
        <div style={{ fontSize: 20, fontWeight: 700, margin: '4px 0' }}>{formatNumber(cumNet)}원</div>
        <div style={{ color: '#888', fontSize: 11 }}>총 {cumCount}건 · 가동률 {cumOccupancy}% ({cumOccupiedDays}/{cumTotalDays}일)</div>
      </div>

      {/* Paid / Unpaid */}
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ ...cardStyle, flex: 1 }} onClick={() => {
          const paidBookings = bookings.filter((b) => b.payment_status === 'completed');
          setPopupData({
            title: '누적 입금',
            rows: [
              ...paidBookings.map((b) => {
                const gross = (b.total_rent || 0) + (b.total_maintenance || 0) + (b.total_cleaning || 0);
                const amount = b.negotiated_price ? b.negotiated_price : gross;
                const net = amount - (b.platform_fee || 0);
                return { label: `${b.guest_name || '게스트'} (${b.check_in.slice(5)})`, value: `${formatNumber(net)}원` };
              }),
              { label: '합계', value: `${formatNumber(paidTotal)}원`, bold: true },
            ],
          });
        }}>
          <div style={{ fontWeight: 500, marginBottom: 2 }}>누적 입금</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#4caf50' }}>{formatNumber(paidTotal)}원</div>
        </div>
        <div style={{ ...cardStyle, flex: 1 }} onClick={() => {
          const unpaidBookings = bookings.filter((b) => b.payment_status !== 'completed');
          setPopupData({
            title: '입금 예정',
            rows: [
              ...unpaidBookings.map((b) => {
                const gross = (b.total_rent || 0) + (b.total_maintenance || 0) + (b.total_cleaning || 0);
                const amount = b.negotiated_price ? b.negotiated_price : gross;
                const net = amount - (b.platform_fee || 0);
                return { label: `${b.guest_name || '게스트'} (${b.check_in.slice(5)})`, value: `${formatNumber(net)}원` };
              }),
              { label: '합계', value: `${formatNumber(unpaidTotal)}원`, bold: true },
            ],
          });
        }}>
          <div style={{ fontWeight: 500, marginBottom: 2 }}>입금 예정</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#e53935' }}>{formatNumber(unpaidTotal)}원</div>
        </div>
      </div>

      {/* Cumulative expense */}
      <div style={cardStyle} onClick={() => toggleCard('expense')}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 500 }}>누적 지출</span>
          <span style={{ fontSize: 10, color: '#aaa' }}>▼</span>
        </div>
        <div style={{ fontSize: 15, fontWeight: 700 }}>-{formatNumber(cumTotalExpense)}원</div>
        {expandedCards['expense'] && (
          <div style={{ marginTop: 8, borderTop: '1px solid #eee', paddingTop: 8 }}>
            <Row label="플랫폼 수수료" value={`-${formatNumber(cumPlatformFee)}원`} />
            <Row label="청소비 실 지출" value={`-${formatNumber(cumCleaningExpense)}원`} />
            {isSublease && <Row label="전대 월세" value={`-${formatNumber(cumSubleaseRent)}원`} />}
            <Row label="관리비" value={`-${formatNumber(cumMaintFee)}원`} />
            <div style={{ borderTop: '1px solid #ddd', margin: '6px 0' }} />
            <div style={{ fontSize: 11, color: '#666', fontWeight: 600, marginBottom: 4 }}>월별 관리비 입력</div>
            {(() => {
              // 연도별로 그룹화
              const byYear: Record<number, { m: number; eKey: string; mFee: number; mRent: number }[]> = {};
              for (let y = 2021, m = 0; y < endY || (y === endY && m <= endM); m++) {
                if (m > 11) { m = 0; y++; }
                const eKey = `${y}-${String(m + 1).padStart(2, '0')}`;
                const mFee = monthlyExpenses[eKey]?.maintenance_fee || 0;
                const mRent = (monthlyExpenses[eKey] as any)?.sublease_rent || 0;
                if (!byYear[y]) byYear[y] = [];
                byYear[y].push({ m, eKey, mFee, mRent });
              }
              const years = Object.keys(byYear).map(Number).sort((a, b) => b - a).filter(y => y >= 2023); // 2021~2022 숨김
              return years.map(y => {
                const isCurrentYear = y === endY;
                const isExpanded = isCurrentYear || expandedExpenseYears.has(y);
                const months = byYear[y].slice().reverse(); // 최신 월 먼저
                return (
                  <div key={y}>
                    {!isCurrentYear && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedExpenseYears(prev => {
                            const next = new Set(prev);
                            next.has(y) ? next.delete(y) : next.add(y);
                            return next;
                          });
                        }}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', cursor: 'pointer', borderTop: '1px solid #f1f5f9', marginTop: 4 }}
                      >
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{y}년</span>
                        <span style={{ fontSize: 10, color: '#94a3b8' }}>{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    )}
                    {isExpanded && months.map(({ m: mo, eKey, mFee, mRent }) => (
                      <div key={eKey} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0' }}>
                        <span style={{ fontSize: 11, minWidth: 36 }}>{String(mo + 1).padStart(2, '0')}월</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {isSublease && (
                            <span style={{ fontSize: 10, color: '#888' }}>월세 {formatNumber(calcSubleaseRent(subleaseRentFull, y, mo, opStart, lastBookingCheckout || undefined))}</span>
                          )}
                          <span style={{ fontSize: 10, color: '#888' }}>관리비</span>
                          <input
                            type="text"
                            defaultValue={mFee ? formatNumber(mFee) : ''}
                            placeholder="0"
                            onClick={(e) => e.stopPropagation()}
                            onBlur={(e) => saveMonthlyExpense(eKey, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                            style={{ width: 52, padding: '2px 3px', border: '1px solid #ddd', borderRadius: 4, fontSize: 10, textAlign: 'right' }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      {/* Net profit */}
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ ...cardStyle, flex: 1 }} onClick={() => setPopupData({
          title: '누적 순이익',
          rows: [
            { label: '누적 수입 (입금+예정)', value: `${formatNumber(paidTotal + unpaidTotal)}원`, bold: true },
            { label: '플랫폼 수수료', value: `-${formatNumber(cumPlatformFee)}원` },
            { label: '청소비 실지출', value: `-${formatNumber(cumCleaningExpense)}원` },
            ...(isSublease ? [{ label: '전대 월세', value: `-${formatNumber(cumSubleaseRent)}원` }] : []),
            { label: '관리비', value: `-${formatNumber(cumMaintFee)}원` },
            { label: '총 지출', value: `-${formatNumber(cumTotalExpense)}원`, bold: true },
            { label: '순이익', value: `${formatNumber(cumNetProfit)}원`, bold: true },
          ],
        })}>
          <div style={{ fontWeight: 500, marginBottom: 2 }}>누적 순이익</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: cumNetProfit >= 0 ? '#1a1a1a' : '#e53935' }}>
            {formatNumber(cumNetProfit)}원
          </div>
        </div>
        <div style={{ ...cardStyle, flex: 1 }} onClick={() => setPopupData({
          title: '세팅비 차감 순이익',
          rows: [
            { label: '누적 순이익', value: `${formatNumber(cumNetProfit)}원` },
            ...(room.setup_brokerage ? [{ label: '중개비', value: `-${formatNumber(room.setup_brokerage)}원` }] : []),
            ...(room.setup_interior ? [{ label: '인테리어', value: `-${formatNumber(room.setup_interior)}원` }] : []),
            ...(room.setup_cleaning ? [{ label: '청소비(세팅)', value: `-${formatNumber(room.setup_cleaning)}원` }] : []),
            { label: '세팅비 합계', value: `-${formatNumber(setupTotal)}원`, bold: true },
            { label: '세팅비 포함 순이익', value: `${formatNumber(netWithSetup)}원`, bold: true },
          ],
        })}>
          <div style={{ fontSize: 10, fontWeight: 500, marginBottom: 2, color: '#64748b' }}>세팅비 차감 순이익</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: netWithSetup >= 0 ? '#1a1a1a' : '#e53935' }}>
            {formatNumber(netWithSetup)}원
          </div>
        </div>
      </div>

      {/* Popup (c1: settle-popup) */}
      {popupData && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setPopupData(null); }}
        >
          <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,.15)', padding: '20px 24px', minWidth: isMobile ? 'auto' : 260, maxWidth: isMobile ? 'calc(100vw - 32px)' : 340, width: isMobile ? 'calc(100vw - 32px)' : 'auto', fontSize: 13, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{popupData.title}</div>
            {popupData.rows?.map((r: any, i: number) => {
              if (r.divider) return <div key={i} style={{ borderTop: '1px solid #eee', margin: '8px 0' }} />;
              if (r.header) return <div key={i} style={{ textAlign: 'center', fontSize: 11, color: '#888', marginBottom: 4 }}>{r.label}</div>;
              if (r.bold) return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontWeight: 700 }}>
                  <span>{r.label}</span><span>{r.value}</span>
                </div>
              );
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ color: '#888' }}>{r.label}</span><span>{r.value}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontWeight: bold ? 600 : 400 }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}
